export const uid = (prefix = "id") =>
  `${prefix}_${(globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)).replace(/-/g, "").slice(0, 12)}`;

const now = () => new Date().toISOString();

export const SCENE_STATUS = ["draft", "review", "approved"];
export const NODE_TYPES = ["text", "reference", "image", "video"];
export const CHARACTER_KINDS = ["character", "product", "scenario", "texture", "vehicle", "prop"];
export const RESOURCE_SCOPES = ["project", "global", "pending_review"];
export const JOB_STATUS = ["blocked", "pending", "running", "done", "failed", "cancelled"];

export const Project = (p = {}) => ({
  id: uid("prj"),
  name: "",
  description: "",
  format: "16:9",
  isDemo: false,
  linkedResourceIds: [], // IDs de Character o Reference globales expresamente vinculados
  activeScriptId: null, // ID del guion activo del proyecto
  activePlan: null, // Plan de producción activo del proyecto (versión, contenido, snapshot, etc.)
  createdAt: now(),
  updatedAt: now(),
  ...p,
});

export const ProductionPlan = (plan = {}) => ({
  id: plan.id || uid("plan"),
  projectId: plan.projectId || null,
  title: plan.title || "Plan de Producción",
  version: plan.version || "v1",
  status: plan.status || "active", // "active", "draft", "outdated"
  summary: plan.summary || "",
  content: plan.content || "",
  sections: Array.isArray(plan.sections) ? plan.sections : [],
  messageId: plan.messageId || null,
  scriptId: plan.scriptId || null,
  scriptVersionId: plan.scriptVersionId || null,
  snapshot: plan.snapshot || null,
  activatedAt: plan.activatedAt || now(),
  createdAt: plan.createdAt || now(),
  updatedAt: now(),
  ...plan,
});

export const Scene = (s = {}) => ({
  id: uid("scn"),
  projectId: null,
  order: 0,
  title: "",
  prompt: "",
  notes: "",
  status: "draft",
  duration: s.duration || null,
  action: s.action || "",
  camera: s.camera || "",
  planned_sound: s.planned_sound || s.sounds || "",
  missingReferences: Array.isArray(s.missingReferences)
    ? s.missingReferences
    : Array.isArray(s.pendingReferences)
    ? s.pendingReferences
    : [],
  scriptId: s.scriptId || null,
  scriptVersionId: s.scriptVersionId || null,
  referenceIds: [],
  assetIds: [],
  selectedAssetId: null,
  isDemo: false,
  createdAt: now(),
  updatedAt: now(),
  ...s,
});

export const Asset = (a = {}) => ({
  id: uid("ast"), projectId: null, sceneId: null, type: "image", url: "", width: null, height: null,
  originalAssetId: null, parentAssetId: null, source: "demo", label: "", isDemo: false, createdAt: now(), ...a,
});

export const CHARACTER_VIEW_TYPES = [
  { id: "front", label: "Frontal" },
  { id: "side", label: "Perfil" },
  { id: "back", label: "Espalda" },
  { id: "expression", label: "Expresión" },
  { id: "other", label: "Otra" },
];

export const Character = (c = {}) => {
  const projectId = c.projectId ?? null;
  const isGlobal = c.scope === "global";
  const scope = isGlobal ? "global" : (projectId ? "project" : (c.isDemo ? "project" : "pending_review"));
  const reviewStatus = (projectId || isGlobal || c.isDemo) ? "confirmed" : "pending_review";

  // Normalizar vistas para compatibilidad bidireccional
  let views = Array.isArray(c.views) ? [...c.views] : [];
  let masterAssetId = c.masterAssetId ?? null;
  let viewAssetIds = Array.isArray(c.viewAssetIds) ? [...c.viewAssetIds] : [];

  if (views.length === 0) {
    if (masterAssetId) {
      views.push({
        id: uid("vw"),
        assetId: masterAssetId,
        viewType: "front",
        isMaster: true,
        label: "Frontal",
      });
    }
    for (const vId of viewAssetIds) {
      if (vId && vId !== masterAssetId && !views.some((v) => v.assetId === vId)) {
        views.push({
          id: uid("vw"),
          assetId: vId,
          viewType: "other",
          isMaster: false,
          label: "Otra",
        });
      }
    }
  } else {
    // Si views está poblado, asegurar que masterAssetId y viewAssetIds reflejan views
    const masterView = views.find((v) => v.isMaster) || views[0];
    if (masterView && masterView.assetId) {
      masterAssetId = masterView.assetId;
      views = views.map((v) => ({
        ...v,
        isMaster: v.id === masterView.id,
      }));
    }
    viewAssetIds = views.map((v) => v.assetId).filter(Boolean);
  }

  const outfit = c.outfit ?? c.baseWardrobe ?? "";
  const id = c.id || uid("chr");

  return {
    id,
    kind: "character",
    name: "",
    handle: "",
    description: "",       // Rasgos físicos e identidad
    outfit,                // Vestuario base separado de la identidad
    baseWardrobe: outfit,  // Alias para compatibilidad con cinematic_memory
    masterAssetId,
    viewAssetIds,
    views,
    projectId,
    scope,
    reviewStatus,
    version: 1,
    isDemo: false,
    createdAt: now(),
    updatedAt: now(),
    ...c,
    id,
    // Asegurar campos normalizados
    masterAssetId,
    viewAssetIds,
    views,
    outfit,
    baseWardrobe: outfit,
    projectId,
    scope,
    reviewStatus,
  };
};

export const Reference = (r = {}) => {
  const projectId = r.projectId ?? null;
  const isGlobal = r.scope === "global";
  const scope = isGlobal ? "global" : (projectId ? "project" : (r.isDemo ? "project" : "pending_review"));
  const reviewStatus = (projectId || isGlobal || r.isDemo) ? "confirmed" : "pending_review";
  const id = r.id || uid("ref");

  return {
    id,
    kind: "character",
    characterId: null,
    assetId: null,
    label: "",
    projectId,
    scope,
    reviewStatus,
    isDemo: false,
    createdAt: now(),
    updatedAt: now(),
    ...r,
    id,
    projectId,
    scope,
    reviewStatus,
  };
};

export const CanvasNode = (n = {}) => ({
  id: uid("nod"), projectId: null, type: "text", x: 80, y: 80, data: {}, createdAt: now(), ...n,
});

export const CanvasEdge = (e = {}) => ({ id: uid("edg"), projectId: null, sourceId: null, targetId: null, role: null, createdAt: now(), ...e });

export const GenerationJob = (j = {}) => ({
  id: uid("job"), projectId: null, sceneId: null, nodeId: null, kind: "image", status: "blocked",
  connectionId: null, model: null, prompt: "", params: {}, referenceIds: [], externalId: null, resultAssetId: null,
  costEstimate: null, costReal: null, note: "", isDemo: false, createdAt: now(), updatedAt: now(), ...j,
});

export const AssistantThread = (th = {}) => ({ id: uid("thr"), projectId: null, messages: [], createdAt: now(), ...th });

export const AssistantMessage = (m = {}) => ({ id: uid("msg"), role: "assistant", content: "", isDemo: false, proposals: [], createdAt: now(), ...m });

export const UsageEntry = (u = {}) => ({ id: uid("use"), jobId: null, provider: null, amount: 0, currency: "USD", label: "demo", createdAt: now(), ...u });

export const ProjectScript = (s = {}) => ({
  id: uid("scr"),
  projectId: null,
  versionId: s.versionId || "v1",
  title: s.title || "Borrador de guion",
  content: s.content || "",
  sourceMessageId: s.sourceMessageId || null,
  isActive: Boolean(s.isActive),
  createdAt: now(),
  updatedAt: now(),
  ...s,
});
